import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Send } from "lucide-react";
import { Button } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { canCheckOut, type PanelPet } from "../panel/panelModel";
import { DailyObservationsSection } from "./DailyObservationsSection";
import { PetHistorySection } from "./PetHistorySection";
import { StayActivitiesSection } from "./StayActivitiesSection";
import { StayCancelActions } from "./StayCancelActions";
import { StayPlanAlerts } from "../plans/StayPlanAlerts";
import { StayCheckoutStep } from "./StayCheckoutStep";
import { StayComandaSection } from "./StayComandaSection";
import { StayMediaSection } from "./StayMediaSection";
import { StayPetDialog } from "./StayPetDialog";
import { openWhatsAppWithText } from "../reservation/comandaQuoteText";
import { buildStayCheckoutText } from "./checkoutText";
import { useStayCheckoutSummary } from "./useStayCheckoutSummary";
import { useCheckOutStay } from "./useCheckOutStay";
import { useStayComanda } from "./useStayComanda";
import { useStayMediaShareToken } from "./useStayMediaShare";
import { StayCoverageEditor } from "./StayCoverageEditor";

export interface FollowUpDialogProps {
  pet: PanelPet;
  isoDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Mesma casca e mesmas seções compartilhadas do check-in. A partir do último
 * dia da reserva ganha uma segunda etapa: a conferência de saída, que troca o
 * conteúdo do mesmo Dialog em vez de abrir outro por cima.
 */
export function FollowUpDialog({ pet, isoDate, open, onOpenChange }: FollowUpDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const [step, setStep] = useState<"follow" | "checkout">("follow");
  const [deliveredItemIds, setDeliveredItemIds] = useState<string[]>([]);
  const [isSendingFinalizedSummary, setIsSendingFinalizedSummary] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const checkOut = useCheckOutStay();
  const module: StayPlanModule = pet.nature === "hotel" ? "hotel" : "daycare";
  const showCheckout = canCheckOut(pet, isoDate);
  const readOnly = pet.status === "checked_out";
  const comanda = useStayComanda(pet.reservationId, readOnly);
  const checkoutSummary = useStayCheckoutSummary(pet.reservationId, pet.petId);
  const shareToken = useStayMediaShareToken(pet.reservationId);

  const closeDialog = (next: boolean) => {
    if (!next) {
      setStep("follow");
      setDeliveredItemIds([]);
    }
    onOpenChange(next);
  };

  const confirmCheckout = async () => {
    await checkOut.mutateAsync({
      stayDayId: pet.stayDayId,
      deliveredItemIds,
      module,
      reservationId: pet.reservationId,
      checkoutDate: isoDate,
    });
    toast({ title: t("panel.checkoutDone") });
    closeDialog(false);

    const saleId = comanda.data?.saleId;
    if (!saleId || !comanda.data?.total) return;

    navigate("/sales/new", {
      state: {
        resumeSale: {
          saleId,
          context: "stayComanda",
          returnTo: `${location.pathname}${location.search}`,
          prefill: {
            petId: pet.petId,
            ownerId: pet.ownerId,
            petName: pet.petName,
            ownerName: pet.ownerName,
            reservationId: pet.reservationId,
          },
        },
      },
    });
  };

  const sendFinalizedSummary = async () => {
    const summary = checkoutSummary.data;
    if (!summary || isSendingFinalizedSummary) return;

    const targetWindow = shareToken.data ? null : window.open("about:blank", "_blank");
    if (targetWindow?.opener) targetWindow.opener = null;
    setIsSendingFinalizedSummary(true);

    try {
      const token = shareToken.data ?? (await shareToken.refetch()).data;
      if (!token) {
        targetWindow?.close();
        return;
      }

      const text = buildStayCheckoutText({
        petName: pet.petName,
        moduleLabel: t(`schedule.nature.${pet.nature}`),
        periodLabel: pet.stayRange ?? isoDate,
        days: summary.days,
        deliveredItems: [],
        comandaLines: [],
        galleryUrl: `${window.location.origin}/fotos/${token}`,
        labels: {
          header: t("panel.checkoutSummaryHeader"),
          activities: t("panel.checkoutActivities"),
          observations: t("panel.checkoutObservations"),
          deliveredItems: t("panel.checkoutDeliveredItems"),
          comanda: t("panel.sectionComanda"),
          photos: t("panel.checkoutPhotosLabel"),
          done: t("panel.checkoutActivityDone"),
          pending: t("panel.checkoutActivityPending"),
        },
        formatDay: (day) => `${day.slice(8, 10)}/${day.slice(5, 7)}`,
      });
      openWhatsAppWithText({ phone: pet.ownerPhone, text, targetWindow });
    } catch {
      targetWindow?.close();
    } finally {
      setIsSendingFinalizedSummary(false);
    }
  };

  if (step === "checkout") {
    return (
      <StayPetDialog
        pet={pet}
        isoDate={isoDate}
        open={open}
        onOpenChange={closeDialog}
        title={t("panel.checkoutTitle", { name: pet.petName })}
        defaultPetCardOpen={false}
        petProfileAlert={<StayPlanAlerts module={module} petId={pet.petId} date={isoDate} comandaLines={comanda.data?.lines ?? []} />}
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button variant="outline" onClick={() => setStep("follow")}>{t("panel.checkoutBack")}</Button>
            <Button disabled={checkOut.isPending} loading={checkOut.isPending} onClick={() => void confirmCheckout()}>
              {t("panel.checkoutConfirm")}
            </Button>
          </div>
        }
      >
        <StayCheckoutStep
          pet={pet}
          isoDate={isoDate}
          deliveredItemIds={deliveredItemIds}
          onDeliveredItemsChange={setDeliveredItemIds}
        />
      </StayPetDialog>
    );
  }

  return (
    <StayPetDialog
      pet={pet}
      isoDate={isoDate}
      open={open}
      onOpenChange={closeDialog}
      title={t("panel.followTitle", { name: pet.petName })}
      readOnlySchedule={readOnly}
      defaultPetCardOpen={!readOnly}
      petProfileAlert={<StayPlanAlerts module={module} petId={pet.petId} date={isoDate} comandaLines={comanda.data?.lines ?? []} />}
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <Button variant="outline" onClick={() => closeDialog(false)}>{t("panel.close")}</Button>
          {showCheckout && (
            <Button onClick={() => setStep("checkout")}>
              <LogOut aria-hidden="true" size={18} /> {t("panel.checkoutAction")}
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <StayActivitiesSection petId={pet.petId} day={isoDate} canComplete={!readOnly} />
        <DailyObservationsSection reservationId={pet.reservationId} petId={pet.petId} petName={pet.petName} day={isoDate} readOnly={readOnly} />
      </div>
      <StayMediaSection reservationId={pet.reservationId} petId={pet.petId} day={isoDate} readOnly={readOnly} />
      {!readOnly && (
        <StayCoverageEditor
          reservationId={pet.reservationId}
          petId={pet.petId}
          ownerId={pet.ownerId}
          module={module}
          checkInDate={isoDate}
        />
      )}
      {readOnly && (
        <Button className="w-full" onClick={() => void sendFinalizedSummary()} disabled={checkoutSummary.isLoading || checkoutSummary.isError || !checkoutSummary.data || isSendingFinalizedSummary}>
          <Send aria-hidden="true" size={16} /> {t("panel.checkoutSendWhatsApp")}
        </Button>
      )}
      <StayComandaSection
        reservationId={pet.reservationId}
        readOnly={readOnly}
      />
      {!readOnly && (
        <StayCancelActions
          reservationId={pet.reservationId}
          stayDayId={pet.stayDayId}
          module={module}
          stayDate={isoDate}
          stayDayStatus={pet.status}
          hasFutureReservations={Boolean(pet.lastStayDate && pet.lastStayDate > isoDate)}
          onCancelled={() => closeDialog(false)}
        />
      )}
    </StayPetDialog>
  );
}
